import { type ContextType, createContext, useContext } from 'react'

import type { EmbeddingPartnerId } from '@orus.eu/embedding-partners'

/**
 * Names of all pharaoh design-system themes — the built-in `light` theme plus
 * a per-partner entry for partners that ship a full token-level design override
 * (e.g. `sidecare`, `revital`, `phenix`).
 *
 * Partners whose customization is limited to logo/font/avatar do NOT appear
 * here; they are expressed as a {@link CoBranding} payload only and run on
 * `light`.
 *
 * Every entry but `light` must be a registered {@link EmbeddingPartnerId} and must have a token set in
 * `@orus.eu/design-tokens`; an id with no token set stays out of this list and renders on `light`.
 */
export const themeNames = [
  'light',
  'sidecare',
  'superindep',
  'propulse',
  'jump',
  'shine',
  'legalstart',
  'legalplace',
  'dougs',
  'previi',
  'qonto',
  'dealt',
  'resco',
  'indy',
  'revital',
  'phenix',
  'auforumassurance',
  'obat',
  'tiime',
] as const satisfies readonly ('light' | EmbeddingPartnerId)[]

/**
 * The active pharaoh theme (token set: colors, typescale, spacing, radii,
 * strokes, shadows). Distinct from {@link CoBranding}, which carries partner
 * assets (logo, avatar, fonts, illustrations).
 */
export type ThemeName = (typeof themeNames)[number]

const themeContext = createContext<{ setTheme: (theme: ThemeName) => void; theme: ThemeName | null }>({
  theme: null,
  setTheme: () => {},
})
export const ThemeContextProvider = themeContext.Provider

/** Returns the active pharaoh theme and its setter. */
export function usePharaohTheme(): ContextType<typeof themeContext> {
  return useContext(themeContext)
}

/** Type guard for narrowing a string (e.g. URL parameter) to a registered {@link ThemeName}. */
export const isPharaohTheme = (theme: string): theme is ThemeName => themeNames.includes(theme as ThemeName)
